/**
 * The transfer entries of the files-mode toolbar (Task 7.15; ordered like every storage kind since UI/UX fix round A):
 * Upload 100 (files / folder, plus the policy for names that already exist — Overwrite is refused on NFS), New folder
 * 110 (a real directory), Download folder 160 (the current folder, after the built-in Download at 150) and, under
 * "more", Delete 520 (the selection). Each
 * is disabled with the ONE gate's reason (`fileActionReason`: read-only → attachment → snapshot → credential → unknown
 * protocol → protocol) — hoverable and focusable, the reason as its tooltip (`ReasonButton` / `reasonMenuItemProps`,
 * review D F5 / P3). The files tab passes `capabilities: { tabId, protocol }`.
 */
import { useState } from 'react';
import { Download, FolderPlus, Trash2, Upload } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { ReasonButton } from '../../../../components/ui/ReasonButton';
import { reasonMenuItemProps } from '../../../../components/ui/reasonMenuItem';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import type { DirectoryRef, FileRef, FileShareProtocolState, ShareRef, TransferCollisionPolicy } from '../../../../types/azureStorage';
import { useStorageTabConnection } from '../hooks/useStorageTabConnection';
import { registerStorageToolbarExtension, TOOLBAR_OVERFLOW_PRIORITY, type StorageToolbarContext } from '../toolbar/toolbarExtensions';
import { StorageToolbarMenuItem } from '../toolbar/StorageToolbarMenuItem';
import { NewDirectoryDialog } from './dialogs/NewDirectoryDialog';
import { confirmFileDelete } from './fileDeleteConfirm';
import { fileActionReason } from './fileGates';
import { isFileWriteOperation } from '../../../../core/engines/azureStorageFileCoverage';
import { requestFileDownload, requestFileUpload } from './fileTransfers';

const SELECT_TO_DELETE = 'Select an item to delete';
const COLLISION_CHOICES: ReadonlyArray<{ value: TransferCollisionPolicy; label: string }> = [
  { value: 'ask', label: 'Ask each time' }, { value: 'skip', label: 'Skip' }, { value: 'rename', label: 'Keep both' }, { value: 'overwrite', label: 'Overwrite' },
];

/** What every entry reads from the toolbar context: the connection, the tab's protocol, the folder shown, its snapshot. */
function useFileToolbarFacts(ctx: StorageToolbarContext) {
  const connection = useStorageTabConnection(ctx.connectionId);
  const protocol: FileShareProtocolState = ctx.capabilities.protocol === 'smb' || ctx.capabilities.protocol === 'nfs' ? ctx.capabilities.protocol : 'unknown';
  const tabId = typeof ctx.capabilities.tabId === 'string' ? ctx.capabilities.tabId : undefined;
  const folder: ShareRef | DirectoryRef | undefined = ctx.resource.kind === 'share' || ctx.resource.kind === 'directory' ? ctx.resource : undefined;
  const snapshot = folder?.snapshot || ('snapshot' in ctx.root ? ctx.root.snapshot : undefined) || undefined;
  // C1 (N9): a refused data role (the listing's error) refuses writes with the contributor role and the reads that need
  // the same listing (Download folder) with the reader role — the file tab passed neither before.
  const writeRefusal = typeof ctx.capabilities.writeRefusal === 'string' ? ctx.capabilities.writeRefusal : undefined;
  const readRefusal = typeof ctx.capabilities.readRefusal === 'string' ? ctx.capabilities.readRefusal : undefined;
  const reasonFor = (op: Parameters<typeof fileActionReason>[1]) => fileActionReason(connection, op, { protocol, snapshot }) ?? (isFileWriteOperation(op) ? writeRefusal : readRefusal);
  return { protocol, tabId, folder, reasonFor };
}

function FileUploadMenu({ ctx }: { ctx: StorageToolbarContext }) {
  const { tabId, folder, reasonFor } = useFileToolbarFacts(ctx);
  const [policy, setPolicy] = useState<TransferCollisionPolicy>('ask');
  const reason = reasonFor('upload-new');
  const overwriteReason = reasonFor('upload-overwrite');
  const collision = policy === 'overwrite' && overwriteReason ? 'ask' : policy;
  const upload = (sourceKind: 'files' | 'folder') => {
    if (folder && !reason) void requestFileUpload({ connectionId: ctx.connectionId, tabId, destination: folder, sourceKind, collision, source: 'toolbar' });
  };
  // An unavailable Upload is not a menu trigger at all: a plain, inert button that shows why.
  if (reason) return <ReasonButton variant="outline" size="xs" className="gap-1" reason={reason} data-testid="file-toolbar-upload"><Upload className="h-3.5 w-3.5" /> Upload</ReasonButton>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="xs" className="gap-1" title="Upload files or a folder" data-testid="file-toolbar-upload">
          <Upload className="h-3.5 w-3.5" /> Upload
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onSelect={() => upload('files')} data-testid="file-upload-files">Upload files…</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => upload('folder')} data-testid="file-upload-folder">Upload folder…</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">If a file already exists</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={collision} onValueChange={(value) => { if (!(value === 'overwrite' && overwriteReason)) setPolicy(value as TransferCollisionPolicy); }}>
          {COLLISION_CHOICES.map(({ value, label }) => {
            const refused = value === 'overwrite' ? overwriteReason : undefined;
            // C1 (N7): the NFS refusal is a visible second line, not only a title.
            return <DropdownMenuRadioItem key={value} value={value} onSelect={(event) => event.preventDefault()} {...reasonMenuItemProps(refused)} data-testid={`file-upload-collision-${value}`}>
              <span className="flex flex-col"><span className={refused ? 'text-muted-foreground' : undefined}>{label}</span>{refused && <span className="text-xs leading-snug text-muted-foreground" data-testid="storage-menu-item-reason">{refused}</span>}</span>
            </DropdownMenuRadioItem>;
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function useNewFolderAction(ctx: StorageToolbarContext): { reason?: string; open: () => void } {
  const { protocol, tabId, folder, reasonFor } = useFileToolbarFacts(ctx);
  const reason = reasonFor('create-directory');
  const open = () => {
    if (!folder || reason || protocol === 'unknown') return;
    ctx.openDialog(<NewDirectoryDialog connectionId={ctx.connectionId} tabId={tabId} parent={folder} protocol={protocol} onClose={() => ctx.openDialog(null)} onCreated={ctx.refresh} />);
  };
  return { reason, open };
}

function NewFolderButton({ ctx }: { ctx: StorageToolbarContext }) {
  const { reason, open } = useNewFolderAction(ctx);
  return <ReasonButton variant="outline" size="xs" className="gap-1" reason={reason} title="Create a folder here" onClick={open} data-testid="file-toolbar-new-folder"><FolderPlus className="h-3.5 w-3.5" /> New folder</ReasonButton>;
}

function useDownloadFolderAction(ctx: StorageToolbarContext): { reason?: string; run: () => void } {
  const { tabId, folder, reasonFor } = useFileToolbarFacts(ctx);
  // R8 (C1): "no folder here" is a reason too, never a native disabled button.
  const reason = reasonFor('download') ?? (folder ? undefined : 'Open a folder to download it');
  return { reason, run: () => { if (folder && !reason) void requestFileDownload({ connectionId: ctx.connectionId, tabId, resources: [folder] }); } };
}

function DownloadFolderButton({ ctx }: { ctx: StorageToolbarContext }) {
  const { reason, run } = useDownloadFolderAction(ctx);
  return <ReasonButton variant="outline" size="xs" className="gap-1" reason={reason} title="Download everything in this folder" onClick={run} data-testid="file-toolbar-download-folder"><Download className="h-3.5 w-3.5" /> Download folder</ReasonButton>;
}

/** The More-menu forms of New folder and Download folder when the action row runs out of width (C1 R5 / N2). */
function NewFolderMenuItem({ ctx }: { ctx: StorageToolbarContext }) {
  const { reason, open } = useNewFolderAction(ctx);
  return <StorageToolbarMenuItem icon={FolderPlus} label="New folder…" reason={reason} onSelect={open} testId="file-toolbar-new-folder" />;
}

function DownloadFolderMenuItem({ ctx }: { ctx: StorageToolbarContext }) {
  const { reason, run } = useDownloadFolderAction(ctx);
  return <StorageToolbarMenuItem icon={Download} label="Download folder" reason={reason} onSelect={run} testId="file-toolbar-download-folder" />;
}

/** A More-menu entry (UI/UX fix round A, A4): a real menu item, last and after a separator (destructive). */
function DeleteMenuItem({ ctx }: { ctx: StorageToolbarContext }) {
  const { tabId, reasonFor } = useFileToolbarFacts(ctx);
  const selection = ctx.selection.filter((ref): ref is DirectoryRef | FileRef => ref.kind === 'directory' || ref.kind === 'file');
  const reason = reasonFor('delete-file') ?? (selection.length === 0 ? SELECT_TO_DELETE : undefined);
  return <StorageToolbarMenuItem icon={Trash2} label="Delete…" shortcut="Del" destructive reason={reason} onSelect={() => void confirmFileDelete({ connectionId: ctx.connectionId, tabId, resources: selection, source: 'toolbar', refresh: ctx.refresh })} testId="file-toolbar-delete" />;
}

const inFolder = (ctx: StorageToolbarContext): boolean => ctx.mode === 'files' && (ctx.resource.kind === 'share' || ctx.resource.kind === 'directory');

export function registerFileToolbarExtensions(): () => void {
  const unregister = [
    // UI/UX fix round A (U7): the one action order of every storage kind — Upload, New folder, (Download), Download folder.
    registerStorageToolbarExtension({ id: 's7-upload', placement: 'primary', order: 100, isVisible: inFolder, render: (ctx) => <FileUploadMenu ctx={ctx} /> }),
    registerStorageToolbarExtension({
      id: 's7-new-folder', placement: 'primary', order: 110, isVisible: inFolder, render: (ctx) => <NewFolderButton ctx={ctx} />,
      overflow: { priority: TOOLBAR_OVERFLOW_PRIORITY.newFolder, renderMenuItem: (ctx) => <NewFolderMenuItem ctx={ctx} /> },
    }),
    registerStorageToolbarExtension({
      id: 's7-download', placement: 'primary', order: 160, isVisible: inFolder, render: (ctx) => <DownloadFolderButton ctx={ctx} />,
      overflow: { priority: TOOLBAR_OVERFLOW_PRIORITY.downloadFolder, renderMenuItem: (ctx) => <DownloadFolderMenuItem ctx={ctx} /> },
    }),
    registerStorageToolbarExtension({ id: 's7-delete', placement: 'more', destructive: true, order: 520, isVisible: (ctx) => ctx.mode === 'files', render: (ctx) => <DeleteMenuItem ctx={ctx} /> }),
  ];
  return () => unregister.forEach((dispose) => dispose());
}
